import { useMemo, useState } from "react"
import { Badge, Col, Row, Spinner } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import { useShowrooms } from "hooks/showroomHook"
import { usePrayerTrackerReport } from "hooks/prayerLogHook"

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

const now = new Date()

const rateColor = rate => {
  if (rate == null) return "secondary"
  if (rate >= 85) return "success"
  if (rate >= 60) return "warning"
  return "danger"
}

const PrayerTrackerReport = () => {
  document.title = "Prayer Tracker"

  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [showroomId, setShowroomId] = useState(null)

  const { data: showroomResp } = useShowrooms({ per_page: 200 })
  const showroomOptions = useMemo(() => {
    const list = showroomResp?.data?.data ?? showroomResp?.data ?? []
    return list.map(s => ({ value: s.id, label: s.name }))
  }, [showroomResp])

  const params = useMemo(
    () => ({ year, month, ...(showroomId ? { showroom_id: showroomId } : {}) }),
    [year, month, showroomId]
  )

  const { data: resp, isLoading, isFetching } = usePrayerTrackerReport(params)
  const report = resp?.data
  const rows = report?.rows ?? []

  const yearOptions = [0, 1, 2].map(d => {
    const y = now.getFullYear() - d
    return { value: y, label: String(y) }
  })
  const monthOptions = MONTHS.map((m, i) => ({ value: i + 1, label: m }))

  return (
    <PageWrapper title="Prayer Tracker" breadcrumbItem="Prayer Tracker">
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Select
            options={showroomOptions}
            value={showroomOptions.find(o => o.value === (showroomId ?? report?.showroom?.id)) ?? null}
            onChange={o => setShowroomId(o?.value ?? null)}
            placeholder="Your showroom"
            isClearable
          />
        </Col>
        <Col md={2}>
          <Select
            options={monthOptions}
            value={monthOptions.find(o => o.value === month)}
            onChange={o => setMonth(o.value)}
          />
        </Col>
        <Col md={2}>
          <Select
            options={yearOptions}
            value={yearOptions.find(o => o.value === year)}
            onChange={o => setYear(o.value)}
          />
        </Col>
      </Row>

      <p className="text-muted small">
        Weighted completion: jamaat = 1.0, alone = 0.85, qada = 0.4 of each daily prayer.
        Feeds the <strong>Prayer Completion Rate</strong> KPI metric (weight 5).
      </p>

      {isLoading || isFetching ? (
        <div className="text-center py-5">
          <Spinner size="sm" color="primary" /> Loading…
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-nowrap align-middle">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th className="text-end">Completion</th>
                <th className="text-end">Jamaat</th>
                <th className="text-end">Alone</th>
                <th className="text-end">Qada</th>
                <th className="text-end">Missed</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No data for this period.
                  </td>
                </tr>
              )}
              {rows.map(row => (
                <tr key={row.employee_id}>
                  <td>
                    {row.name}
                    {row.employee_code && (
                      <small className="text-muted d-block">{row.employee_code}</small>
                    )}
                  </td>
                  <td className="text-end">
                    <Badge color={rateColor(row.completion_rate)}>
                      {row.completion_rate == null ? "—" : `${row.completion_rate}%`}
                    </Badge>
                  </td>
                  <td className="text-end">{row.jamaat}</td>
                  <td className="text-end">{row.single}</td>
                  <td className="text-end">{row.kaja}</td>
                  <td className="text-end text-muted">{row.missed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageWrapper>
  )
}

export default PrayerTrackerReport
